import { Preferences } from '@capacitor/preferences';
import type { Flashcard, FlashcardTargetLanguage, FlashcardCategory } from '../types/flashcard';

/**
 * Offline Storage Service
 *
 * Provides local caching and sync queue for offline-first functionality.
 * Uses Capacitor Preferences for persistent storage.
 */

// Storage keys
const STORAGE_KEYS = {
  FLASHCARDS: 'offline_flashcards',
  LANGUAGES: 'offline_languages',
  CATEGORIES: 'offline_categories',
  SYNC_QUEUE: 'offline_sync_queue',
  LAST_SYNC: 'offline_last_sync',
} as const;

// Types for sync queue
export type SyncAction =
  | 'create_flashcard'
  | 'update_flashcard'
  | 'delete_flashcard'
  | 'create_category'
  | 'update_category'
  | 'delete_category';

export interface SyncQueueItem {
  id: string;
  action: SyncAction;
  data: any;
  timestamp: number;
  retryCount: number;
}

export interface OfflineData<T> {
  data: T[];
  lastUpdated: number;
}

class OfflineStorageService {
  /**
   * Generic method to get data from storage
   */
  private async getItem<T>(key: string): Promise<T | null> {
    try {
      const { value } = await Preferences.get({ key });
      if (!value) return null;
      return JSON.parse(value) as T;
    } catch (error) {
      console.error(`Error getting ${key} from storage:`, error);
      return null;
    }
  }

  /**
   * Generic method to set data in storage
   */
  private async setItem<T>(key: string, data: T): Promise<void> {
    try {
      await Preferences.set({
        key,
        value: JSON.stringify(data),
      });
    } catch (error) {
      console.error(`Error setting ${key} in storage:`, error);
      throw error;
    }
  }

  /**
   * Get flashcards from offline storage
   */
  async getFlashcards(): Promise<OfflineData<Flashcard> | null> {
    return this.getItem<OfflineData<Flashcard>>(STORAGE_KEYS.FLASHCARDS);
  }

  /**
   * Save flashcards to offline storage
   */
  async saveFlashcards(flashcards: Flashcard[]): Promise<void> {
    const data: OfflineData<Flashcard> = {
      data: flashcards,
      lastUpdated: Date.now(),
    };
    await this.setItem(STORAGE_KEYS.FLASHCARDS, data);
  }

  /**
   * Get target languages from offline storage
   */
  async getLanguages(): Promise<OfflineData<FlashcardTargetLanguage> | null> {
    return this.getItem<OfflineData<FlashcardTargetLanguage>>(STORAGE_KEYS.LANGUAGES);
  }

  /**
   * Save target languages to offline storage
   */
  async saveLanguages(languages: FlashcardTargetLanguage[]): Promise<void> {
    const data: OfflineData<FlashcardTargetLanguage> = {
      data: languages,
      lastUpdated: Date.now(),
    };
    await this.setItem(STORAGE_KEYS.LANGUAGES, data);
  }

  /**
   * Get categories from offline storage
   */
  async getCategories(): Promise<OfflineData<FlashcardCategory> | null> {
    return this.getItem<OfflineData<FlashcardCategory>>(STORAGE_KEYS.CATEGORIES);
  }

  /**
   * Save categories to offline storage
   */
  async saveCategories(categories: FlashcardCategory[]): Promise<void> {
    const data: OfflineData<FlashcardCategory> = {
      data: categories,
      lastUpdated: Date.now(),
    };
    await this.setItem(STORAGE_KEYS.CATEGORIES, data);
  }

  /**
   * Get sync queue
   */
  async getSyncQueue(): Promise<SyncQueueItem[]> {
    const queue = await this.getItem<SyncQueueItem[]>(STORAGE_KEYS.SYNC_QUEUE);
    return queue || [];
  }

  /**
   * Add item to sync queue
   */
  async addToSyncQueue(action: SyncAction, data: any): Promise<void> {
    const queue = await this.getSyncQueue();
    const item: SyncQueueItem = {
      id: `${action}_${Date.now()}_${Math.random()}`,
      action,
      data,
      timestamp: Date.now(),
      retryCount: 0,
    };
    queue.push(item);
    await this.setItem(STORAGE_KEYS.SYNC_QUEUE, queue);
  }

  /**
   * Remove item from sync queue
   */
  async removeFromSyncQueue(itemId: string): Promise<void> {
    const queue = await this.getSyncQueue();
    const filtered = queue.filter(item => item.id !== itemId);
    await this.setItem(STORAGE_KEYS.SYNC_QUEUE, filtered);
  }

  /**
   * Update retry count for a sync queue item
   */
  async updateSyncQueueItemRetry(itemId: string): Promise<void> {
    const queue = await this.getSyncQueue();
    const updated = queue.map(item =>
      item.id === itemId
        ? { ...item, retryCount: item.retryCount + 1 }
        : item
    );
    await this.setItem(STORAGE_KEYS.SYNC_QUEUE, updated);
  }

  /**
   * Clear sync queue
   */
  async clearSyncQueue(): Promise<void> {
    await this.setItem(STORAGE_KEYS.SYNC_QUEUE, []);
  }

  /**
   * Get last sync timestamp
   */
  async getLastSync(): Promise<number | null> {
    return this.getItem<number>(STORAGE_KEYS.LAST_SYNC);
  }

  /**
   * Update last sync timestamp
   */
  async updateLastSync(): Promise<void> {
    await this.setItem(STORAGE_KEYS.LAST_SYNC, Date.now());
  }

  /**
   * Clear all offline data (for logout or reset)
   */
  async clearAll(): Promise<void> {
    await Promise.all([
      Preferences.remove({ key: STORAGE_KEYS.FLASHCARDS }),
      Preferences.remove({ key: STORAGE_KEYS.LANGUAGES }),
      Preferences.remove({ key: STORAGE_KEYS.CATEGORIES }),
      Preferences.remove({ key: STORAGE_KEYS.SYNC_QUEUE }),
      Preferences.remove({ key: STORAGE_KEYS.LAST_SYNC }),
    ]);
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(): Promise<{
    flashcardsCount: number;
    languagesCount: number;
    categoriesCount: number;
    queueLength: number;
    lastSync: number | null;
  }> {
    const [flashcards, languages, categories, queue, lastSync] = await Promise.all([
      this.getFlashcards(),
      this.getLanguages(),
      this.getCategories(),
      this.getSyncQueue(),
      this.getLastSync(),
    ]);

    return {
      flashcardsCount: flashcards?.data.length || 0,
      languagesCount: languages?.data.length || 0,
      categoriesCount: categories?.data.length || 0,
      queueLength: queue.length,
      lastSync,
    };
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorageService();
